import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import BuyNowPanel from '@/components/BuyNowPanel';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Calendar, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ConfirmationPage({ name, lovedOneName }) {
  const [calendlyUrl, setCalendlyUrl] = useState('');

  useEffect(() => {
    axios.get(`${API}/settings/public`).then(r => setCalendlyUrl(r.data.calendly_url || '')).catch(() => {});
  }, []);

  const firstName = name?.split(' ')[0] || '';

  return (
    <div className="min-h-screen bg-[#FCFBF9]">
      <Navbar />
      <section className="pt-32 sm:pt-40 pb-16 sm:pb-20">
        <div className="max-w-2xl mx-auto px-6 sm:px-12">
          {/* Confirmation */}
          <div className="text-center mb-16 sm:mb-20 animate-fade-in-up">
            <div className="w-14 h-14 border border-[#A89F91] flex items-center justify-center mx-auto mb-8">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#A89F91" strokeWidth="1.5"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <h1 data-testid="contact-success-title" className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917] leading-tight mb-6">
              You're all set{firstName ? `, ${firstName}` : ''}.
            </h1>
            <p className="text-base sm:text-lg text-[#57534E] leading-relaxed max-w-lg mx-auto">
              We've received your request to preserve {lovedOneName ? <>{lovedOneName}'s</> : "your loved one's"} story. We'll review your information and follow up shortly.
            </p>
          </div>

          {/* Divider */}
          <div className="w-16 h-[1px] bg-[#D6D3CD] mx-auto mb-16 sm:mb-20" />

          {/* Scheduling Section */}
          <div className="text-center animate-fade-in-up animation-delay-400" data-testid="scheduling-section">
            <p className="overline mb-4">Move Faster</p>
            <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-medium text-[#1C1917] mb-4">
              Want to get started sooner?
            </h2>
            <p className="text-base text-[#57534E] leading-relaxed max-w-md mx-auto mb-8">
              If you'd like to move forward quickly, you can schedule a brief consultation with our team now.
            </p>

            {calendlyUrl ? (
              <div data-testid="calendly-embed-section">
                <a
                  href={calendlyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="schedule-call-button"
                  className="inline-flex items-center justify-center gap-3 bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] px-10 py-4 text-sm font-medium tracking-wide transition-all duration-300 mb-6"
                >
                  <Calendar size={16} /> Schedule a Consultation
                </a>
                <div className="mt-8 border border-[#E7E5DF] bg-white">
                  <iframe
                    src={calendlyUrl}
                    className="w-full h-[580px] border-0"
                    title="Schedule a consultation"
                    data-testid="calendly-iframe"
                  />
                </div>
              </div>
            ) : (
              <div className="border border-[#E7E5DF] p-8 sm:p-10 bg-white" data-testid="scheduling-placeholder">
                <div className="flex items-center justify-center gap-3 text-[#A89F91] mb-4">
                  <Calendar size={24} />
                </div>
                <p className="font-['Cormorant_Garamond'] text-xl text-[#1C1917] mb-2">Scheduling coming soon</p>
                <p className="text-sm text-[#78716C] leading-relaxed max-w-sm mx-auto">
                  Our team will reach out within 24 hours to schedule your consultation. If you'd prefer to connect sooner, reply to our follow-up email.
                </p>
              </div>
            )}
            <p className="text-sm italic text-[#A89F91] mt-6">We are currently scheduling sessions for the coming weeks.</p>
          </div>

          {/* What to Expect */}
          <div className="mt-16 sm:mt-20 border-t border-[#E7E5DF] pt-12 sm:pt-16 text-center animate-fade-in-up animation-delay-600">
            <p className="overline mb-6">What Happens Next</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center max-w-xl mx-auto">
              <div>
                <span className="font-['Cormorant_Garamond'] text-2xl font-light text-[#D6D3CD] block mb-2">01</span>
                <p className="text-sm text-[#57534E]">We review your information</p>
              </div>
              <div>
                <span className="font-['Cormorant_Garamond'] text-2xl font-light text-[#D6D3CD] block mb-2">02</span>
                <p className="text-sm text-[#57534E]">A coordinator reaches out</p>
              </div>
              <div>
                <span className="font-['Cormorant_Garamond'] text-2xl font-light text-[#D6D3CD] block mb-2">03</span>
                <p className="text-sm text-[#57534E]">We plan your session together</p>
              </div>
            </div>
          </div>

          {/* Soft CTA */}
          <div className="mt-16 sm:mt-20 text-center">
            <p className="font-['Cormorant_Garamond'] text-lg italic text-[#78716C] mb-6">
              While you wait, see what your family's heirloom could look like.
            </p>
            <Link to="/sample" data-testid="confirmation-sample-link" className="inline-flex items-center gap-2 text-sm text-[#57534E] border border-[#D6D3CD] hover:border-[#A89F91] hover:text-[#1C1917] px-6 py-3 transition-all duration-300">
              See a Sample Heirloom <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default function Contact() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'buy' ? 'buy' : 'inquire';
  const [tab, setTab] = useState(initialTab);
  const [form, setForm] = useState({ name: '', email: '', phone: '', loved_one_name: '', relationship: '', market: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [markets, setMarkets] = useState([]);

  // Keep URL ?tab=... in sync with the active tab (cleanly back/forward-able)
  useEffect(() => {
    const current = searchParams.get('tab');
    if (tab === 'buy' && current !== 'buy') {
      const next = new URLSearchParams(searchParams); next.set('tab', 'buy');
      setSearchParams(next, { replace: true });
    } else if (tab === 'inquire' && current === 'buy') {
      const next = new URLSearchParams(searchParams); next.delete('tab');
      setSearchParams(next, { replace: true });
    }
  }, [tab, searchParams, setSearchParams]);

  useEffect(() => {
    axios.get(`${API}/markets`).then(r => setMarkets(r.data)).catch(() => {});
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.loved_one_name || !form.relationship || !form.market) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSubmitted(true);
      toast.success('Your inquiry has been received.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <ConfirmationPage name={form.name} lovedOneName={form.loved_one_name} />;
  }

  return (
    <div className="min-h-screen bg-[#FCFBF9]">
      <SEO
        title="Get Started"
        path="/contact"
        description="Begin preserving your loved one's legacy. Tell us about them and we'll reach out to schedule a consultation. Serving Austin, Dallas, Houston, and more."
      />
      <Navbar />
      <section className="pt-32 sm:pt-40 pb-24 sm:pb-32">
        <div className="max-w-2xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-10 sm:mb-12">
            <p className="overline mb-6">Get Started</p>
            <h1 data-testid="contact-title" className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl font-medium text-[#1C1917] leading-tight mb-6">Preserve Their Story</h1>
            <p className="text-base sm:text-lg text-[#57534E] leading-relaxed">
              {tab === 'buy'
                ? 'Choose a package to begin. We\'ll reach out within one business day to schedule your discovery call.'
                : 'Tell us about your loved one. We\'ll reach out to discuss how we can preserve their legacy.'}
            </p>
          </div>

          {/* Tab toggle */}
          <div className="flex items-center justify-center mb-12 sm:mb-14" data-testid="get-started-tabs">
            <div className="inline-flex border border-[#D6D3CD] bg-white" role="tablist">
              <button
                role="tab"
                aria-selected={tab === 'inquire'}
                onClick={() => setTab('inquire')}
                data-testid="tab-inquire"
                className={`px-7 sm:px-10 py-3 text-sm tracking-wide transition-all duration-300 ${
                  tab === 'inquire'
                    ? 'bg-[#1C1917] text-[#FCFBF9]'
                    : 'text-[#57534E] hover:text-[#1C1917]'
                }`}
              >
                Inquire
              </button>
              <button
                role="tab"
                aria-selected={tab === 'buy'}
                onClick={() => setTab('buy')}
                data-testid="tab-buy"
                className={`px-7 sm:px-10 py-3 text-sm tracking-wide transition-all duration-300 border-l border-[#D6D3CD] ${
                  tab === 'buy'
                    ? 'bg-[#1C1917] text-[#FCFBF9]'
                    : 'text-[#57534E] hover:text-[#1C1917]'
                }`}
              >
                Buy Now
              </button>
            </div>
          </div>

          {tab === 'buy' ? (
            <BuyNowPanel />
          ) : (
          <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-2">Your Name *</label>
                <input name="name" value={form.name} onChange={handleChange} data-testid="contact-name" className="w-full bg-transparent border-b border-[#D6D3CD] focus:border-[#1C1917] py-3 text-[#1C1917] placeholder-[#A89F91] outline-none transition-colors duration-300 text-base" placeholder="Full name" required />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-2">Email *</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} data-testid="contact-email" className="w-full bg-transparent border-b border-[#D6D3CD] focus:border-[#1C1917] py-3 text-[#1C1917] placeholder-[#A89F91] outline-none transition-colors duration-300 text-base" placeholder="your@email.com" required />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-2">Phone</label>
                <input name="phone" value={form.phone} onChange={handleChange} data-testid="contact-phone" className="w-full bg-transparent border-b border-[#D6D3CD] focus:border-[#1C1917] py-3 text-[#1C1917] placeholder-[#A89F91] outline-none transition-colors duration-300 text-base" placeholder="(Optional)" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-2">What city is your loved one located in? *</label>
                <select name="market" value={form.market} onChange={handleChange} data-testid="contact-market" className="w-full bg-transparent border-b border-[#D6D3CD] focus:border-[#1C1917] py-3 text-[#1C1917] outline-none transition-colors duration-300 text-base appearance-none" required>
                  <option value="" disabled>Select their area</option>
                  {markets.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                  <option value="Not listed / Outside current service areas">Not listed / Outside current service areas</option>
                </select>
                <p className="text-xs text-[#A89F91] mt-2 leading-relaxed">All interviews are conducted in person, so this helps us confirm availability in their area.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-2">Loved One's Name *</label>
                <input name="loved_one_name" value={form.loved_one_name} onChange={handleChange} data-testid="contact-loved-one-name" className="w-full bg-transparent border-b border-[#D6D3CD] focus:border-[#1C1917] py-3 text-[#1C1917] placeholder-[#A89F91] outline-none transition-colors duration-300 text-base" placeholder="Their name" required />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-2">Relationship *</label>
                <input name="relationship" value={form.relationship} onChange={handleChange} data-testid="contact-relationship" className="w-full bg-transparent border-b border-[#D6D3CD] focus:border-[#1C1917] py-3 text-[#1C1917] placeholder-[#A89F91] outline-none transition-colors duration-300 text-base" placeholder="e.g., Mother, Grandfather" required />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-[#78716C] mb-2">Notes</label>
              <textarea name="notes" value={form.notes} onChange={handleChange} data-testid="contact-notes" rows={3} className="w-full bg-transparent border-b border-[#D6D3CD] focus:border-[#1C1917] py-3 text-[#1C1917] placeholder-[#A89F91] outline-none transition-colors duration-300 text-base resize-none" placeholder="Anything you'd like us to know..." />
            </div>

            <div className="pt-4">
              <button type="submit" disabled={submitting} data-testid="contact-submit-button" className="w-full sm:w-auto inline-flex items-center justify-center bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] disabled:opacity-50 px-10 py-4 text-sm font-medium tracking-wide transition-all duration-300 gap-2">
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : 'Start Your Family\'s Heirloom'}
              </button>
            </div>
          </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
